/* <kenra-article-body> — Kenra blog article body video fix (WEB-6319).
 *
 * The Contentful→Shopify blog migration left inline videos double-encoded in
 * `article.content`: each video appears once as a literal
 * `[[video]]={"link":"…"}` shortcode paragraph — sitting in its correct spot in
 * the flow but rendered as dead literal text — and again as a real responsive
 * <iframe>, with all the iframes clumped together at the end of the body. The
 * marketing site (Gatsby) expanded the shortcode in place at runtime; this
 * element reproduces that so the videos land where the author placed them:
 *
 *   1. Replace each `[[video]]` shortcode paragraph with a responsive embed.
 *   2. Remove the duplicated trailing iframe for any video we expanded in place,
 *      so each video renders once, in the right spot.
 *
 * Degrades safely: an article with only real iframes (no shortcode) is left
 * untouched; a shortcode whose link yields no YouTube id is left as-is. */
if (!customElements.get('kenra-article-body')) {
  customElements.define(
    'kenra-article-body',
    class KenraArticleBody extends HTMLElement {
      connectedCallback() {
        const SHORTCODE = /^\s*\[\[video\]\]\s*=\s*(\{[\s\S]*\})\s*$/;

        // Pull the YouTube id out of an embed/watch/short URL. Returns '' for
        // non-YouTube URLs and for the `videoseries` playlist placeholder,
        // which isn't a playable single-video id.
        const youTubeId = (url) => {
          if (!url) return '';
          const match = String(url).match(
            /(?:youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([\w-]{6,})/
          );
          const id = match ? match[1] : '';
          return id === 'videoseries' ? '' : id;
        };

        const buildEmbed = (id, title) => {
          const wrap = document.createElement('div');
          wrap.className = 'kenra-article__video';
          const frame = document.createElement('iframe');
          frame.className = 'kenra-article__video-frame';
          frame.src = 'https://www.youtube.com/embed/' + id;
          frame.title = title || 'YouTube video player';
          frame.loading = 'lazy';
          frame.setAttribute('frameborder', '0');
          frame.setAttribute(
            'allow',
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          );
          frame.setAttribute('allowfullscreen', '');
          wrap.appendChild(frame);
          return wrap;
        };

        // Snapshot the iframes that shipped in the body BEFORE we add any, and
        // map id → title so the in-place embeds keep the real accessible name.
        const original = Array.from(this.querySelectorAll('iframe[src*="/embed/"]'));
        const titleById = new Map();
        original.forEach((frame) => {
          const id = youTubeId(frame.getAttribute('src'));
          if (id && frame.title && !titleById.has(id)) titleById.set(id, frame.title);
        });

        // 1. Expand each `[[video]]` shortcode paragraph in place.
        const expanded = new Set();
        this.querySelectorAll('p').forEach((p) => {
          const payload = (p.textContent || '').match(SHORTCODE);
          if (!payload) return;
          let link;
          try {
            link = JSON.parse(payload[1]).link;
          } catch {
            return; // malformed payload — leave the paragraph untouched
          }
          const id = youTubeId(link);
          if (!id) return;
          p.replaceWith(buildEmbed(id, titleById.get(id)));
          expanded.add(id);
        });

        // 2. Remove the now-duplicated original iframe(s) for each video we
        // re-created in place. Only the originals snapshotted above are
        // candidates, so the freshly built embeds are never touched. Drop the
        // iframe's wrapper only when it's a <div> wrapping *just* this iframe
        // (the migrated `<div style="padding-bottom:56.25%">…</div>` case);
        // otherwise remove the iframe alone — never walk up to a shared
        // container like `.kenra-article__body`, which is itself a <div>.
        original.forEach((frame) => {
          const id = youTubeId(frame.getAttribute('src'));
          if (!id || !expanded.has(id)) return;
          const wrapper = frame.parentElement;
          if (wrapper && wrapper !== this && wrapper.tagName === 'DIV' && wrapper.children.length === 1) {
            wrapper.remove();
          } else {
            frame.remove();
          }
        });
      }
    }
  );
}
